/**
 * The world panel's vocabulary — the words for the four enums `WorldSnapshot` carries
 * as machine values.
 *
 * **These are not display joins and they are not thresholds.** `LocationVisitState`,
 * `Relationship` and `NPCStatus` arrive raw because they are not localized on the wire the way
 * `band_label` and `category_label` are — they are a closed set of states with no per-story
 * text behind them, so naming them is the client's job in the same way "Health" and "Stamina"
 * are (`vitals.ts`). The i18n pass replaced the strings with `ui.world.*` from `/translations`; the
 * shape it replaces them in is this one.
 *
 * Every map is `Record<Enum, …>` and therefore exhaustive: a state the API adds is a typecheck
 * error here rather than a card that renders a blank (D5). That is the same rule
 * `changeFamilies.ts` and `VitalBar.tsx` are written to, and it is the reason this file exists
 * at all instead of four inline ternaries.
 *
 * The three small helpers at the foot are not vocabulary — they are the formatting and the one
 * key→name join that more than one card needs. They live here because the alternative is
 * exporting them from a file of components, which is a fast-refresh boundary and, more to the
 * point, the wrong place to look for them.
 */
import type { LocationVisitState, NpcStatus, Relationship, WorldLocation } from '@/api/generated';
import type { UiKey } from '@/i18n/en.generated';
import type { Translate } from '@/i18n/t';

/** How loudly a card is drawn. `alarm` is the only one that gets colour, as in the feed. */
export type WorldTone = 'alarm' | 'good' | 'plain';

const TONE_COLOR: Record<WorldTone, string | undefined> = {
  alarm: 'var(--color-ramp-danger)',
  good: 'var(--color-ramp-ok)',
  plain: undefined,
};

export function toneColor(tone: WorldTone): string | undefined {
  return TONE_COLOR[tone];
}

/**
 * Every badge in the codex is an engine enum, so each is a key plus a tone: the *word* is
 * chrome and localizable, the *colour* is a judgement this file makes and no locale changes.
 * The world's own nouns beside them — place names, NPC names, descriptions — are the DM's and
 * arrive in the story's language already.
 */
export interface WorldBadge {
  label: UiKey;
  tone: WorldTone;
}

const VISIT_STATES: Record<LocationVisitState, WorldBadge> = {
  // Not a degree of searching: the character has been told this place exists and has never
  // stood in it. Plain rather than alarming — a lead is not a warning.
  rumoured: { label: 'ui.world.visit.rumoured', tone: 'plain' },
  unexplored: { label: 'ui.world.visit.unexplored', tone: 'plain' },
  partially_searched: { label: 'ui.world.visit.partially_searched', tone: 'plain' },
  fully_searched: { label: 'ui.world.visit.fully_searched', tone: 'plain' },
  // The two the player is meant to notice at a glance in a list of nine places.
  dangerous: { label: 'ui.world.visit.dangerous', tone: 'alarm' },
  safe: { label: 'ui.world.visit.safe', tone: 'good' },
};

export function visitState(state: LocationVisitState): WorldBadge {
  return VISIT_STATES[state];
}

const RELATIONSHIPS: Record<Relationship, WorldBadge> = {
  hostile: { label: 'ui.world.relationship.hostile', tone: 'alarm' },
  unfriendly: { label: 'ui.world.relationship.unfriendly', tone: 'plain' },
  neutral: { label: 'ui.world.relationship.neutral', tone: 'plain' },
  friendly: { label: 'ui.world.relationship.friendly', tone: 'good' },
  allied: { label: 'ui.world.relationship.allied', tone: 'good' },
};

export function relationship(value: Relationship): WorldBadge {
  return RELATIONSHIPS[value];
}

/**
 * Where a relationship sits on the hostile→allied axis, as a signed distance from neutral
 * (`TODO.md` §4.6).
 *
 * The *word* says which of five states this is; the number is what lets one card be drawn on
 * the same axis as every other card. Factions and people both carry a `Relationship` and the
 * only quantity beside it — `reputation` — belongs to factions alone, so a shared picture has
 * to be built out of the enum both of them have rather than out of the number one of them
 * has.
 *
 * Signed rather than 0–4 because the sign is the half that matters: it is the difference
 * between *drifting toward hostile* and *drifting toward allied*, and a bar drawn from the
 * middle outward shows it before a word is read. Same argument as `VitalBar`'s two
 * directions.
 *
 * A `Record` for the same reason as the three tables above: a sixth relationship is a
 * typecheck error here, not a card silently drawn at the origin (D5).
 */
const RELATIONSHIP_STEPS: Record<Relationship, number> = {
  hostile: -2,
  unfriendly: -1,
  neutral: 0,
  friendly: 1,
  allied: 2,
};

/** The axis itself, neutral at the middle — the cells a scale draws, in order. */
export const RELATIONSHIP_AXIS: readonly number[] = [-2, -1, 0, 1, 2];

export function relationshipStep(value: Relationship): number {
  return RELATIONSHIP_STEPS[value];
}

const NPC_STATUSES: Record<NpcStatus, WorldBadge | null> = {
  // Alive is the default state of a person and says nothing; the other three are news.
  alive: null,
  dead: { label: 'ui.world.npc.dead', tone: 'alarm' },
  missing: { label: 'ui.world.npc.missing', tone: 'alarm' },
  unknown: { label: 'ui.world.npc.unknown', tone: 'plain' },
};

export function npcStatus(value: NpcStatus): WorldBadge | null {
  return NPC_STATUSES[value];
}

/** `+34` / `−41`, with the engine's own minus sign rather than a hyphen. */
export function reputationLabel(value: number): string {
  if (value === 0) return '0';
  return `${value > 0 ? '+' : '−'}${String(Math.abs(value))}`;
}

/** The chapter a thing was last seen in — `interaction_chapters` and friends are ascending. */
export function lastChapter(chapters: readonly number[]): number | null {
  return chapters.at(-1) ?? null;
}

/**
 * `held by: The Scrappers` — a place's faction *keys*, joined to the names the factions list
 * knows them by, falling back to the key for a faction the place names and the player has
 * never met.
 *
 * Here rather than beside the card that draws it because the "here, now" block draws the same
 * line for the same place, and two spellings of one join is how the codex would start
 * contradicting itself between its top and its middle.
 */
export function heldBy(
  t: Translate,
  place: WorldLocation,
  factionNames: ReadonlyMap<string, string>,
): string {
  return t('ui.world.held_by', {
    factions: place.factions.map((key) => factionNames.get(key) ?? key).join(', '),
  });
}
